import {
  component,
  computed,
  observable,
  observableArray,
  subscribe,
} from 'knockout-decorators'

import {
  CartResponse,
  CartQueueCheckResponse,
  CartQueueAlternateResponse,
  CartQueueAlternateOption,
  CartQueueChoice,
  CartQueueAlternateChoices,
  CartQueueChoicesRequest,
  CartStatus,
  Offer,
} from '@tixa/schema'
import { Analytics, ErrorReporter, Language } from '~/utils'
import { pageState } from '~/store/page'
import { EventState } from '~/store/event'
import UnfocusFlash from '~/utils/title-effects'
import { logger } from '~/utils/log'

import QueueIcon from '~/images/icons/cart_waiting.svg?raw'
import source from './template.html?raw'
import { CartState } from '../state'

type PartialCartResponse = Partial<CartResponse> & { status: CartStatus }

@component('cart-queue', source)
export class WaitingRoom {
  private queueIcon = QueueIcon
  private Tracker = Analytics.getInstance()
  private Locale = Language.getInstance()

  private cartId = '-1'
  private queueCheckTimeout: ReturnType<typeof setTimeout> | number | null =
    null
  private cartState: CartState = CartState.get()

  @observable public isQueueLoading = false
  @observable public queueLocation = 0
  @observable public queueCheckErrors = 0
  private readonly QueueErrorLimit: number = 5

  // Queue status
  @observable public queueStatus: 'queue' | 'wait' | 'alternate' =
    CartStatus.queue
  @observableArray public queueTicketStatus: CartQueueAlternateOption[] = []
  @computed get ticketsWithAlternates(): number {
    return this.queueTicketStatus.filter((s) => s.alternate !== undefined)
      .length
  }

  // Alternate offers
  @observableArray public alternates: CartQueueAlternateOption[] = []
  @observableArray public alternateOriginals: { [k: string]: Offer } = {}
  @observable public isMandatoryErrorVisible = false
  @computed get hasAlternateOffers(): boolean {
    return this.alternates.length > 0
  }

  private alternateForm: HTMLFormElement | null = null
  private acceptTimeoutToken: ReturnType<typeof setTimeout> | null = null
  private timeStartedAccepting = 0
  private timeSpentAccepting = 0
  private isLoadingAcceptResponse = false

  private finished: ((cart: CartResponse) => void) | null = null
  private error: ((cart: PartialCartResponse) => void) | null = null

  constructor(params: {
    cartId: string
    finished: (cart: CartResponse) => void
    error: (cart: PartialCartResponse) => void
  }) {
    if (this.Tracker.gotIntoQueue) {
      this.Tracker.gotIntoQueue()
    }
    this.finished = params.finished
    this.error = params.error
    this.cartId = params.cartId
    this.timeStartedAccepting = 0
    this.timeSpentAccepting = 0
    this.nextQueueCheck()
    subscribe<CartResponse>(
      () => this.cartState.content,
      (response) => {
        if (!response) {
          this.closeQueue()
        }
      }
    )
  }

  private closeQueue() {
    logger.debug('QUEUE', 'Close')
    this.stopQueueChecking()
    this.queueLocation = 0
    this.queueCheckErrors = 0
  }

  private nextQueueCheck() {
    this.stopQueueChecking()
    this.queueCheckTimeout = setTimeout(() => {
      if (!this.queueCheckTimeout) {
        return
      }
      this.checkQueue()
    }, 5000)
  }

  private stopQueueChecking() {
    if (this.queueCheckTimeout) {
      clearTimeout(this.queueCheckTimeout)
      clearInterval(this.queueCheckTimeout)
      this.queueCheckTimeout = null
    }
  }

  private async checkQueue() {
    logger.debug('QUEUE', 'Checking status')
    this.isQueueLoading = true
    let response: CartResponse | null = null
    try {
      response = await pageState.api.waitingRoom.checkQueue(this.cartId)
    } catch (error) {
      if (error instanceof Error) {
        ErrorReporter.exception(error)
      }
      this.queueCheckErrors++
      if (this.QueueErrorLimit === this.queueCheckErrors) {
        if (this.error) {
          this.error({ status: CartStatus.error } as PartialCartResponse)
        }
      } else {
        this.nextQueueCheck()
      }
    } finally {
      this.isQueueLoading = false
    }

    if (response && this.queueCheckTimeout) {
      this.processResponse(response)
    }
  }
  private leftQueue = () => {
    logger.debug('QUEUE', 'User has left')
    if (this.Tracker.leftQueue) {
      this.Tracker.leftQueue()
    }
    this.closeQueue()
  }
  private reload() {
    window.location.reload()
  }

  private ticketPrice(offer: Offer) {
    return Language.formatCurrency(offer.price, offer.priceCurrency)
  }

  private processResponse(response: CartResponse) {
    if (!this.queueCheckTimeout) {
      return
    }
    this.isQueueLoading = false
    if (response.id) {
      this.cartId = response.id.toString()
    }
    this.responseProcessors[response.status](response)
  }

  private responseProcessors: Record<CartStatus, (CartResponse) => void> = {
    wait: (response) => this.wait(response),
    alternate: (response) => this.alternate(response),
    cart: (response) => this.proceedToCart(response),
    couponused: (response) => this.errored(response),
    soldout: (response) => this.soldOut(response),
    error: (response) => this.errored(response),
    // these cart statuses are not handled
    queue: () => {
      /**/
    },
    onecart: () => {
      /**/
    },
  }

  private wait(response: CartResponse) {
    logger.debug('QUEUE', 'Wait')
    const queueResponse = response as CartQueueCheckResponse
    this.queueStatus = queueResponse.status
    if (queueResponse.queue) {
      this.queueLocation = queueResponse.queue
    }
    this.queueTicketStatus = queueResponse.queueTickets || []
    this.nextQueueCheck()
  }

  private alternate(response: CartResponse) {
    logger.debug('QUEUE', 'Must choose from alternates')
    this.closeQueue()
    const alternateResp = response as CartQueueAlternateResponse
    this.alternates = alternateResp.queueAlternates

    this.queueStatus = alternateResp.status
    this.timeStartedAccepting = Date.now()
    if (response.expiry) {
      this.setupAlternateExpiryCheck(response.expiry)
    }
    UnfocusFlash('🎫')
  }

  private proceedToCart(response: CartResponse) {
    logger.debug('QUEUE', 'Proceed to cart')
    if (this.Tracker.finishQueue) {
      this.Tracker.finishQueue()
    }
    this.closeQueue()
    if (response.expiry) {
      response.expiry = response.expiry - this.timeSpentAccepting
    }
    this.timeSpentAccepting = 0
    if (this.finished) {
      UnfocusFlash('🎫')
      this.finished(response)
    }
  }

  private soldOut(response: CartResponse) {
    logger.debug('QUEUE', 'Sold out')
    if (this.Tracker.queueSoldout) this.Tracker.queueSoldout()
    this.errored(response)
  }

  private errored(response: CartResponse) {
    logger.debug('QUEUE', 'Error')
    this.closeQueue()
    if (this.error) {
      this.error(response)
    }
  }

  private alternateFormLoaded(element: HTMLFormElement) {
    this.alternateForm = element
  }

  private originalOffer = (id: string) => {
    return EventState.get().offerById(id)
  }

  private setupAlternateExpiryCheck = (expiry: number) => {
    logger.debug(
      'QUEUE',
      'Start measuring alternate accept expriy',
      (expiry / 1000).toFixed(2),
      'seconds remaining'
    )
    this.acceptTimeoutToken = setTimeout(this.alternateCartExpired, expiry)
  }

  private alternateCartExpired = () => {
    logger.debug('QUEUE', 'Time to accept alternates expired')
    if (this.acceptTimeoutToken) {
      clearTimeout(this.acceptTimeoutToken)
    }
    this.acceptTimeoutToken = null
    if (this.error) this.error(this.Locale.translations.timesup)
  }

  private async acceptAlternateCart() {
    logger.debug('QUEUE', 'Accepted alternates')
    if (
      (this.queueStatus === CartStatus.wait ||
        this.queueStatus === CartStatus.queue) &&
      this.ticketsWithAlternates > 0
    ) {
      await this.acceptAlternatesInQueue()
    } else {
      await this.acceptAlternatesAfterQueue()
    }
  }

  private async acceptAlternatesInQueue() {
    // Accept alternates while in queue
    const request: CartQueueChoicesRequest = {
      cartId: this.cartId,
      choices: this.queueTicketStatus
        .filter((status) => 'alternate' in status)
        .map((status) => {
          if (!status.alternate) {
            throw new Error('No alternate offer found')
          }
          return {
            choice: CartQueueAlternateChoices.Alternate,
            originalOffer: {
              '@type': 'Offer',
              '@id': this.originalOffer(status.id)?.['@id'] || '',
              sku: this.originalOffer(status.id)?.sku || '',
            },
            chosenOffer: {
              '@type': 'Offer',
              '@id': status.alternate['@id'],
              sku: status.alternate.sku,
              eligibleQuantity: {
                '@type': 'QuantitativeValue',
                value: status.alternate.eligibleQuantity.value,
                minValue: status.alternate.eligibleQuantity.minValue,
                maxValue: status.alternate.eligibleQuantity.maxValue,
              },
            },
          }
        }),
    }
    this.stopQueueChecking()

    try {
      this.isLoadingAcceptResponse = true
      const response = await pageState.api.waitingRoom.acceptAlternateCart(
        request
      )
      this.queueCheckTimeout = 1
      this.processResponse(response)
    } catch (error) {
      if (error instanceof Error) {
        ErrorReporter.exception(error)
      }
    } finally {
      this.isLoadingAcceptResponse = false
    }
  }

  private async acceptAlternatesAfterQueue() {
    // Accept alternates when only alternates are available
    // Create request from form data
    if (!this.alternateForm) {
      return
    }
    const formData = new FormData(this.alternateForm)

    const choices = [...formData.entries()].map((entry) => {
      const alt = this.alternates.find((alt) => alt.id === entry[0])
      if (!alt || !alt.alternate) {
        throw new Error('Alternate offer not found')
      }
      const chosenOffer = CartQueueAlternateChoices.Remaining
        ? this.originalOffer(entry[0])
        : alt.alternate

      if (!chosenOffer) {
        throw new Error('Chosen offer not found')
      }

      const ret: CartQueueChoice = {
        choice: entry[1] as CartQueueAlternateChoices,
        originalOffer: {
          '@type': 'Offer',
          '@id': this.originalOffer(entry[0])?.['@id'] || '',
          sku: this.originalOffer(entry[0])?.sku || '',
        },
        chosenOffer: {
          '@type': 'Offer',
          '@id': chosenOffer['@id'],
          sku: chosenOffer.sku,
          eligibleQuantity: {
            '@type': 'QuantitativeValue',
            value: CartQueueAlternateChoices.Remaining
              ? alt.max
              : alt.alternate.eligibleQuantity.value,
            minValue: chosenOffer.eligibleQuantity.minValue,
            maxValue: CartQueueAlternateChoices.Remaining
              ? alt.max
              : alt.alternate.eligibleQuantity.maxValue,
          },
        },
      }

      return ret
    })

    // Stop if not all choices are made
    if (choices.length < this.alternates.length) {
      this.isMandatoryErrorVisible = true
      return
    }

    // All good, proceed to submit
    this.isMandatoryErrorVisible = false
    if (this.acceptTimeoutToken) clearTimeout(this.acceptTimeoutToken)
    this.acceptTimeoutToken = null
    this.stopQueueChecking()

    const request: CartQueueChoicesRequest = {
      cartId: this.cartId,
      choices,
    }

    try {
      this.isLoadingAcceptResponse = true
      const response = await pageState.api.waitingRoom.acceptAlternateCart(
        request
      )
      this.alternates = []
      this.queueCheckTimeout = 1
      this.timeSpentAccepting =
        this.timeSpentAccepting + (Date.now() - this.timeStartedAccepting)
      this.timeStartedAccepting = 0
      this.processResponse(response)
    } catch (error) {
      if (error instanceof Error) {
        ErrorReporter.exception(error)
      }
    } finally {
      this.isLoadingAcceptResponse = false
    }
  }
}
